import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ExternalLink } from "lucide-react";
import { useScrollReveal, useStaggeredReveal } from "@/hooks/useScrollReveal";
import { use3DTilt, useSpotlight } from "@/hooks/use3DTilt";

const projects = [
  {
    title: "Production-Grade AI & Automation Systems",
    role: "CTO & AI Architect at ScaleLogix",
    description: "Built complete AI infrastructure — from voice and chat agents to full CRM and workflow automation.",
    achievements: [
      "AI Voice Agents with Retell AI & Twilio",
      "AI Chat & SDR Agents for outreach automation",
      "Full GoHighLevel CRM setup with pipelines",
      "n8n workflows connecting all systems"
    ],
    skills: ["Retell AI", "OpenAI API", "n8n", "GoHighLevel", "Twilio", "Supabase"],
    highlight: "Full-stack AI Systems"
  },
  {
    title: "Lead Generation Systems",
    role: "Co-Founder at sAIve time",
    description: "Productized lead generation system covering LinkedIn optimization, Sales Navigator integration, outreach automation, cold email, lead scoring, and appointment booking.",
    achievements: [
      "LinkedIn + Sales Navigator integration",
      "Automated outreach with HeyReach & Aimfox",
      "Cold email via Instantly & Smartlead",
      "Full appointment booking automation"
    ],
    skills: ["LinkedIn Sales Navigator", "HeyReach", "Instantly", "Smartlead", "Taplio"],
    highlight: "Full Lead Gen Pipeline"
  },
  {
    title: "AI/ML Citation Link Prediction",
    role: "Machine Learning Engineer",
    description: "Achieved top places in Kaggle competition predicting academic paper citations with 99.33% AUC and 96.24% accuracy through innovative multi-modal feature integration.",
    achievements: [
      "99.33% AUC, 96.24% accuracy",
      "83% memory reduction with GPU optimization",
      "24x speedup through XGBoost optimization",
      "Processes 2M+ paper pairs"
    ],
    skills: ["NLP", "Feature Engineering", "XGBoost", "Python", "GPU Computing"],
    highlight: "Top Kaggle Competition"
  },
  {
    title: "Multimodal MRI Super-Resolution",
    role: "Research Scientist - Diploma Thesis",
    description: "Deep learning system for medical imaging that reconstructs low-quality MRI scans into high-resolution diagnostic images. Improved on state-of-the-art by +0.83 dB.",
    achievements: [
      "PSNR: 41.77 dB (+0.83 dB improvement)",
      "Interpretable deep unfolding architecture",
      "Convergence in 15-50 epochs",
      "University of Ioannina thesis"
    ],
    skills: ["Deep Learning", "PyTorch", "Computer Vision", "Medical Imaging"],
    highlight: "+0.83 dB State-of-the-Art"
  },
  {
    title: "Economic Indicator Prediction",
    role: "Data Science Intern at Ontovisual",
    description: "Built prediction models for World Bank economic indicators and stock prediction. Worked with real messy data including missing values, weird formats, and challenging APIs.",
    achievements: [
      "World Bank data integration",
      "Stock prediction models",
      "Economic indicator forecasting",
      "Production-ready ML pipelines"
    ],
    skills: ["Python", "Machine Learning", "Data Engineering", "APIs", "Pandas"],
    highlight: "Real-World Production ML"
  }
];

const ProjectCard = ({ project, index, isVisible }: { project: typeof projects[0], index: number, isVisible: boolean }) => {
  const tilt = use3DTilt(8);
  const spotlight = useSpotlight();

  return (
    <div
      ref={tilt.ref}
      onMouseMove={(e) => {
        tilt.onMouseMove(e);
        spotlight.onMouseMove(e);
      }}
      onMouseLeave={tilt.onMouseLeave}
      className={`transition-all duration-700 ${
        isVisible
          ? "opacity-100 translate-x-0"
          : index % 2 === 0
          ? "opacity-0 -translate-x-10"
          : "opacity-0 translate-x-10"
      }`}
      style={{ transitionDelay: `${index * 100}ms` }}
    >
      <Card
        ref={spotlight.ref}
        className="card-enhanced spotlight p-8 group relative overflow-hidden"
      >
        {/* Accent line on hover */}
        <div className="absolute top-0 left-0 w-full h-0.5 bg-primary transform origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-500" />

        <div className="relative z-10 space-y-5">
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
            <div className="space-y-2 flex-1">
              <h3 className="text-2xl font-bold group-hover:text-primary transition-colors duration-300">
                {project.title}
              </h3>
              <p className="text-sm text-muted-foreground">{project.role}</p>
            </div>
            {project.highlight && (
              <Badge className="bg-primary text-primary-foreground self-start whitespace-nowrap hover:scale-105 transition-transform duration-300">
                {project.highlight}
              </Badge>
            )}
          </div>

          <p className="text-muted-foreground leading-relaxed">
            {project.description}
          </p>

          <div className="space-y-3">
            <h4 className="font-semibold text-sm uppercase tracking-wider text-primary">
              Key Achievements
            </h4>
            <ul className="grid md:grid-cols-2 gap-2">
              {project.achievements.map((achievement, i) => (
                <li
                  key={i}
                  className="flex items-start gap-2 text-sm"
                >
                  <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-primary/50" />
                  <span className="text-muted-foreground">{achievement}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="flex flex-wrap gap-2 pt-3">
            {project.skills.map((skill, i) => (
              <Badge
                key={i}
                variant="secondary"
                className="text-xs hover:bg-primary hover:text-primary-foreground transition-all duration-300 cursor-default"
              >
                {skill}
              </Badge>
            ))}
          </div>
        </div>
      </Card>
    </div>
  );
};

const Projects = () => {
  const { ref: headerRef, isVisible: headerVisible } = useScrollReveal({ threshold: 0.2 });
  const { containerRef: projectsRef, visibleItems } = useStaggeredReveal(projects.length, 150);

  return (
    <section id="projects" className="py-32 px-6 relative overflow-hidden">
      {/* Background decorations */}
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-border to-transparent" />

      {/* Floating elements */}
      <div className="absolute top-40 left-10 w-72 h-72 bg-primary/5 rounded-full blur-3xl" />
      <div className="absolute bottom-40 right-10 w-96 h-96 bg-primary/5 rounded-full blur-3xl" />

      <div className="container max-w-6xl mx-auto relative z-10">
        <div
          ref={headerRef}
          className={`text-center mb-20 space-y-6 transition-all duration-1000 ${
            headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
          }`}
        >
          <h2 className="text-4xl md:text-5xl font-bold text-violet-400 [text-shadow:0_0_30px_rgba(167,139,250,0.7),0_0_60px_rgba(139,92,246,0.4)]">
            Featured Projects
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
            Delivering measurable impact through cutting-edge AI and engineering solutions
          </p>
        </div>

        <div ref={projectsRef} className="space-y-6">
          {projects.map((project, index) => (
            <ProjectCard
              key={index}
              project={project}
              index={index}
              isVisible={visibleItems[index]}
            />
          ))}
        </div>

        <div className={`text-center mt-16 transition-all duration-1000 delay-500 ${
          headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
        }`}>
          <p className="text-muted-foreground mb-6">
            Want to see more details or discuss collaboration opportunities?
          </p>
          <Button
            size="lg"
            className="shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 group"
            onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
          >
            Get in Touch
            <ExternalLink className="ml-2 w-4 h-4 group-hover:rotate-12 transition-transform duration-300" />
          </Button>
        </div>
      </div>
    </section>
  );
};

export default Projects;
